'use client';

import { useState, useEffect } from 'react';
import type { DiagramMetadata, Folder } from '@/lib/archimate/storage/db';
import { getAllDiagrams, getAllFolders, createFolder, deleteFolder, deleteDiagram } from '@/lib/archimate/storage/db';

interface DiagramBrowserProps {
  currentDiagramId: string;
  onSelectDiagram: (id: string) => void;
  onNewDiagram: () => void;
  onClose: () => void;
}

export function DiagramBrowser({
  currentDiagramId,
  onSelectDiagram,
  onNewDiagram,
  onClose,
}: DiagramBrowserProps) {
  const [diagrams, setDiagrams] = useState<DiagramMetadata[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);

  const loadData = async () => {
    const [diagramList, folderList] = await Promise.all([
      getAllDiagrams(),
      getAllFolders(),
    ]);
    setDiagrams(diagramList);
    setFolders(folderList);
  };

  // Load diagrams and folders
   
  useEffect(() => {
    loadData();
  }, []);
   

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    await createFolder(newFolderName, selectedFolder);
    setNewFolderName('');
    setShowNewFolder(false);
    await loadData();
  };

  const handleDeleteDiagram = async (id: string) => {
    if (confirm('Delete this diagram? This cannot be undone.')) {
      await deleteDiagram(id);
      await loadData();
    }
  };

  const handleDeleteFolder = async (id: string) => {
    if (confirm('Delete this folder? Diagrams will be moved to root.')) {
      await deleteFolder(id);
      await loadData();
    }
  };

  // Filter diagrams by folder
  const filteredDiagrams = selectedFolder
    ? diagrams.filter(d => d.folderId === selectedFolder)
    : diagrams.filter(d => !d.folderId);

  // Get folder hierarchy
  const rootFolders = folders.filter(f => !f.parentId);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background border-2 border-accent rounded-lg w-full max-w-4xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="border-b border-border p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-foreground">Diagram Browser</h2>
          <button
            onClick={onClose}
            className="text-foreground-tertiary hover:text-foreground"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Left: Folder Tree */}
          <div className="w-64 border-r border-border overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground">Folders</h3>
              <button
                onClick={() => setShowNewFolder(true)}
                className="text-xs px-2 py-1 bg-accent text-white rounded hover:bg-accent-hover"
              >
                + New
              </button>
            </div>

            {showNewFolder && (
              <div className="mb-4 p-2 bg-surface rounded">
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateFolder();
                    if (e.key === 'Escape') {
                      setShowNewFolder(false);
                      setNewFolderName('');
                    }
                  }}
                  placeholder="Folder name"
                  autoFocus
                  className="w-full px-2 py-1 bg-background border border-border rounded text-foreground text-sm"
                />
                <div className="flex gap-1 mt-2">
                  <button
                    onClick={handleCreateFolder}
                    className="flex-1 px-2 py-1 bg-accent text-white text-xs rounded"
                  >
                    Create
                  </button>
                  <button
                    onClick={() => {
                      setShowNewFolder(false);
                      setNewFolderName('');
                    }}
                    className="px-2 py-1 bg-surface text-foreground text-xs rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Root folder */}
            <button
              onClick={() => setSelectedFolder(null)}
              className={`w-full text-left px-3 py-2 rounded mb-1 transition-colors ${
                selectedFolder === null
                  ? 'bg-accent text-white'
                  : 'text-foreground hover:bg-surface'
              }`}
            >
              📁 All Diagrams
            </button>

            {/* Folder list */}
            {rootFolders.map((folder) => (
              <div key={folder.id} className="group">
                <button
                  onClick={() => setSelectedFolder(folder.id)}
                  className={`w-full text-left px-3 py-2 rounded mb-1 transition-colors flex items-center justify-between ${
                    selectedFolder === folder.id
                      ? 'bg-accent text-white'
                      : 'text-foreground hover:bg-surface'
                  }`}
                >
                  <span>📂 {folder.name}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteFolder(folder.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 text-xs px-1 hover:text-red-400"
                  >
                    ✕
                  </button>
                </button>
              </div>
            ))}
          </div>

          {/* Right: Diagram List */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground">
                {selectedFolder ? folders.find(f => f.id === selectedFolder)?.name : 'All Diagrams'}
                <span className="ml-2 text-foreground-tertiary">({filteredDiagrams.length})</span>
              </h3>
              <button
                onClick={onNewDiagram}
                className="px-3 py-1.5 bg-accent text-white text-sm rounded hover:bg-accent-hover"
              >
                + New Diagram
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {filteredDiagrams.map((d) => (
                <div
                  key={d.id}
                  className={`group border-2 rounded-lg p-3 cursor-pointer transition-all ${
                    d.id === currentDiagramId
                      ? 'border-accent bg-accent-muted'
                      : 'border-border hover:border-accent bg-surface'
                  }`}
                  onClick={() => {
                    onSelectDiagram(d.id);
                    onClose();
                  }}
                >
                  {/* Thumbnail placeholder */}
                  <div className="aspect-video bg-background rounded mb-2 flex items-center justify-center text-foreground-muted text-xs">
                    Preview
                  </div>

                  <h4 className="text-sm font-medium text-foreground mb-1 truncate">
                    {d.name}
                  </h4>

                  <div className="flex items-center justify-between text-xs text-foreground-tertiary">
                    <span>{d.elementCount} elements</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDiagram(d.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300"
                    >
                      Delete
                    </button>
                  </div>

                  <div className="text-xs text-foreground-muted mt-1">
                    {new Date(d.updatedAt).toLocaleDateString()}
                  </div>
                </div>
              ))}

              {filteredDiagrams.length === 0 && (
                <div className="col-span-full text-center py-12 text-foreground-muted">
                  No diagrams in this folder.
                  <br />
                  <button
                    onClick={onNewDiagram}
                    className="mt-4 text-accent hover:underline"
                  >
                    Create your first diagram
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
